import { type ReactNode, useId, useState } from 'react'
import { formatNumber } from '@eformulieren/engine'
import { amount } from './dossierValue.js'
import type { SingleProps } from './kinds.js'

/**
 * Aantallen en bedragen: `number` en `currency`.
 *
 * Het getalsbereik is een regelsoort (`getalsbereik`, B11) en geen `min`/`max` op
 * het invoerveld: dan meldt de browser in zijn eigen taal en op zijn eigen moment,
 * terwijl de melding uit de regel komt (R-30) en bij het verlaten van het veld
 * hoort (R-25). Wat de control wel zet is `inputmode`, want dat opent het juiste
 * toetsenbord en meldt niets.
 *
 * **Een tekstveld met `inputmode`, geen `type="number"`** (audit R-29). Een getalveld van de browser
 * slikt een letter zonder iets te zeggen, kent in Chrome geen komma als decimaalteken en draait
 * de waarde bij scrollen met het muiswiel. Hier blijft wat de invuller typte staan; bij het
 * verlaten zegt het veld wat er mis is als het geen getal is. De waarde in de stand blijft een
 * getal of leeg, net als voorheen: dat is het dataformaat.
 *
 * **De begrotingsvorm zit hier niet in.** `currency` is één bedrag. De
 * begrotingscontrol met zijn eigen rekenwerk is buiten scope tot de U10-sessie met
 * NWO (ontwerp 6.2), en er komt tot die tijd ook geen voorlopige vorm.
 */

export interface NumberProps extends SingleProps {
  readonly soort: 'number' | 'currency'
}

export function NumberInput({ soort: kind, koppeling: link, waarde: value, onWaarde: onValue, bewerkbaar: editable, taal: language }: NumberProps): ReactNode {
  if (!editable) {
    // Alleen-lezen leest een bedrag als bedrag, "€ 500.000", en niet als "500000" in een grijs
    // invoervak (audit van 30 oktober 2026, R-86). Het element houdt het id van de koppeling, zodat
    // de hulptekst en de meldingen er nog aan hangen.
    const number = typeof value === 'number' ? value : undefined
    return (
      <span className="ef-getal ef-getal--lezen" data-soort={kind} id={link.id} aria-describedby={link['aria-describedby']}>
        {number === undefined
          ? language === 'nl'
            ? 'Niet ingevuld'
            : 'Not filled in'
          : kind === 'currency'
            ? amount(number, language)
            : formatNumber(number, language)}
      </span>
    )
  }
  return <EditableNumber kind={kind} link={link} value={value} onValue={onValue} language={language} />
}

function EditableNumber({
  kind,
  link,
  value,
  onValue,
  language,
}: {
  kind: 'number' | 'currency'
  link: NumberProps['koppeling']
  value: NumberProps['waarde']
  onValue: NumberProps['onWaarde']
  language: NumberProps['taal']
}): ReactNode {
  const noticeId = useId()
  const [draft, setDraft] = useState<string | undefined>(undefined)
  const [left, setLeft] = useState(false)
  const current = typeof value === 'number' ? value : undefined
  // Wat er staat: de eigen invoer zolang die bij de waarde past, anders de waarde zelf (die kan
  // van een medeschrijver komen).
  const shown = draft !== undefined && parseNumber(draft) === current ? draft : current === undefined ? (draft ?? '') : shownNumber(current, language)
  const wrong = left && shown.trim() !== '' && parseNumber(shown) === undefined
  const describedBy = [link['aria-describedby'], wrong ? noticeId : undefined].filter((part) => part !== undefined).join(' ')
  return (
    <span className="ef-getal" data-soort={kind}>
      {/* Boven de invoer, zoals de meldingen van de regels (audit R-28). */}
      {wrong ? (
        <span className="ef-veld__melding ef-veld__melding--hard ef-getal__melding" id={noticeId}>
          {language === 'nl' ? 'Vul een getal in, zonder letters. Bijvoorbeeld 48.' : 'Enter a number, without letters. For example 48.'}
        </span>
      ) : null}
      {kind === 'currency' ? (
        // Het teken staat buiten het invoerveld: het hoort niet in de waarde en is
        // voor een schermlezer al onderdeel van het label van het veld.
        <span className="ef-getal__teken" aria-hidden="true">
          &euro;
        </span>
      ) : null}
      <input
        className="ef-invoer"
        type="text"
        inputMode={kind === 'currency' ? 'decimal' : 'numeric'}
        autoComplete="off"
        {...link}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        {...(wrong ? { 'aria-invalid': true } : {})}
        value={shown}
        onChange={(event) => {
          const raw = event.target.value
          setDraft(raw)
          if (raw.trim() === '') {
            onValue(undefined)
            return
          }
          // Een halve of verkeerde invoer ("4x") gaat niet als getal de stand in; hij blijft wel in
          // beeld, zodat de invuller ziet wat hij typte.
          onValue(parseNumber(raw))
          if (parseNumber(raw) !== undefined) setLeft(false)
        }}
        onBlur={() => setLeft(true)}
      />
    </span>
  )
}

/**
 * Een getal uit wat de invuller typte, of undefined. Een komma is het decimaalteken; staat er een
 * komma, dan zijn punten scheidingstekens voor duizendtallen ("1.250,50"). Zonder komma is een punt
 * het decimaalteken ("1250.50"), behalve in de vorm van duizendtallen ("1.250", "1.250.000", niet beginnend
 * met een nul, zodat "0.125" een achtste blijft). Spaties tellen
 * niet mee.
 */
export function parseNumber(raw: string): number | undefined {
  let text = raw.replace(/\s/g, '')
  if (text === '') return undefined
  if (text.includes(',')) text = text.replace(/\./g, '').replace(',', '.')
  else if (/^-?[1-9]\d{0,2}(\.\d{3})+$/.test(text)) text = text.replace(/\./g, '')
  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(text)) return undefined
  const parsed = Number(text)
  return Number.isFinite(parsed) ? parsed : undefined
}

/** Een opgeslagen getal zoals het in het veld staat: in het Nederlands met een komma. */
function shownNumber(value: number, language: NumberProps['taal']): string {
  const text = String(value)
  return language === 'nl' ? text.replace('.', ',') : text
}
